import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface EmailPreviewProps {
  subject: string | null;
  text: string;
  /**
   * When true, the iframe resizes itself to the rendered body height so it
   * never scrolls internally — the outer page scrolls instead. Requires the
   * iframe to be same-origin, so we relax the sandbox to allow-same-origin
   * (still no scripts, still no forms). Use in full-page contexts (campaign
   * launcher). Leave false in fixed-size modal contexts.
   */
  autoSize?: boolean;
}

const DARK_THEME_CLASSES = ["dark", "dark-contrast", "tech", "navy"];

const EmailPreview: FC<EmailPreviewProps> = ({ subject, text, autoSize }) => {
  const srcDoc = useMemo(() => {
    // Trim the injected HTML so trailing whitespace can't become a text
    // node inside <body>. Without this, an empty whitespace text node
    // inherits body's line-height and adds a phantom ~20px line at the
    // bottom — which scrollHeight reports, inflating the iframe height.
    const body = (text || "").trim();

    // Mirror the app's scrollbar styling inside the iframe so the preview's
    // internal scroll matches the rest of the app. Light mode uses the OS
    // default, dark-family themes get the app's custom 8px scrollbar with
    // colors resolved from the parent's CSS variables (the iframe document
    // can't see them on its own).
    let scrollbarCss = "";
    if (typeof window !== "undefined") {
      const isDarkLike = DARK_THEME_CLASSES.some((c) =>
        document.documentElement.classList.contains(c)
      );
      if (isDarkLike) {
        const root = window.getComputedStyle(document.documentElement);
        const card = root.getPropertyValue("--card").trim() || "#1f2937";
        const border = root.getPropertyValue("--border").trim() || "#374151";
        const textSecondary =
          root.getPropertyValue("--text-secondary").trim() || "#9ca3af";
        scrollbarCss = `
        html { scrollbar-color: ${border} ${card}; scrollbar-width: thin; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: ${card}; }
        ::-webkit-scrollbar-thumb { background-color: ${border}; border-radius: 8px; border: 2px solid ${card}; }
        ::-webkit-scrollbar-thumb:hover { background-color: ${textSecondary}; }
      `;
      }
    }

    return `<!doctype html><html><head><meta charset="utf-8"><style>
        html, body { margin: 0; padding: 0; }
        body { font-family: Inter, system-ui, sans-serif; font-size: 13px; line-height: 1.5; color: #111; background: #fff; }
        body > table { min-height: 100vh; }
        p { margin: 0 0 12px 0; }
        p:last-child { margin-bottom: 0; }
        strong { font-weight: 600; }
        ${scrollbarCss}
      </style></head><body>${body}</body></html>`;
  }, [text]);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [autoHeight, setAutoHeight] = useState<number | null>(null);
  const lastHeightRef = useRef<number>(0);

  const measure = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc || !doc.body) return;
    // Use body.scrollHeight only — not documentElement. With scrolling="no",
    // the iframe's <html> element's scrollHeight grows to match whatever
    // height we set the iframe to, which traps us at our fallback (600px) and
    // never shrinks to actual content.
    const h = doc.body.scrollHeight;
    if (h > 0 && h !== lastHeightRef.current) {
      lastHeightRef.current = h;
      setAutoHeight(h);
    }
  }, []);

  const handleLoad = useCallback(() => {
    if (!autoSize) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    // Tear down any prior timers from a previous load (switching emails
    // reloads the iframe).
    (iframe as any)._cleanup?.();
    // Reset our height-tracking so a smaller new email shrinks correctly.
    lastHeightRef.current = 0;

    // Measure now, then a few staggered re-measures so late font/image layout
    // is caught. No ResizeObserver — the content is static once srcDoc is
    // applied, and the observer caused noisy "loop" warnings in the browser.
    measure();
    const timers = [
      window.setTimeout(measure, 50),
      window.setTimeout(measure, 200),
      window.setTimeout(measure, 600),
    ];

    (iframe as any)._cleanup = () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [autoSize, measure]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      const iframe = iframeRef.current as any;
      iframe?._cleanup?.();
    };
  }, []);

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="flex flex-col gap-1 px-3 py-2 border border-themed-border rounded bg-themed-background">
        <div className="text-[10px] uppercase tracking-wide text-themed-textSecondary">
          Subject
        </div>
        <div className="text-[13px] font-semibold break-words">
          {subject?.trim() ? (
            subject
          ) : (
            <span className="italic font-normal text-themed-textSecondary">
              (no subject)
            </span>
          )}
        </div>
      </div>
      <iframe
        ref={iframeRef}
        title="email preview"
        className={
          autoSize
            ? "w-full bg-white border border-themed-border rounded block"
            : "flex-1 w-full bg-white border border-themed-border rounded"
        }
        sandbox={autoSize ? "allow-same-origin" : ""}
        srcDoc={srcDoc}
        onLoad={autoSize ? handleLoad : undefined}
        style={
          autoSize
            ? { height: autoHeight ? `${autoHeight}px` : "600px" }
            : undefined
        }
        scrolling={autoSize ? "no" : undefined}
      />
    </div>
  );
};

export default EmailPreview;
